import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';

import { OWCC_APP_HASH, OWCC_APP_VERSION } from 'src/build-env';

import { AppContainerComponent } from './core/app-container/app-container.component';
import { DevComponent } from './core/dev/dev.component';
import { LazyHostComponent } from './core/lazy-host/lazy-host.component';
import { LazyComponentId } from './core/services/lazy-components/lazy-components-registry';
import { Theme } from './core/services/theme/theme.enum';
import { ThemeService } from './core/services/theme/theme.service';
import { Components, Directives, Pipes } from './ng.types';
import { ConsentContainerComponent } from './shared/consent-container/consent-container.component';
import { LoadingStatusIndicatorComponent } from './shared/loading-status/loading-status-indicator/loading-status-indicator.component';
import { NotificationComponent } from './shared/notification/notification.component';
import { selectIsInAemPreviewMode, selectIsLoading } from './store/common/common.selectors';
import { WINDOW } from './utils/window.token';

export const APP_COMPONENT_SELECTOR = 'owcc-car-configurator';

@Component({
  // Intentionally not using the prefix 'cc' as this is our external facing root component
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'owcc-car-configurator',
  templateUrl: './app.component.html',
  styleUrls: ['../app.scss', './app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [
    Directives(LetDirective, NgClass, NgIf, RouterOutlet),
    Pipes(AsyncPipe),
    Components(
      ConsentContainerComponent,
      AppContainerComponent,
      DevComponent,
      NotificationComponent,
      LoadingStatusIndicatorComponent,
      LazyHostComponent
    )
  ]
})
export class AppComponent implements OnInit {
  public readonly theme$: Observable<Theme> = this.themeService.theme$.pipe(
    // setTimeout is needed so that change detector runs only after the data-theme attribute is set in the template
    tap(() =>
      this.window.setTimeout(() => {
        this.changeDetectorRef.markForCheck();
      })
    )
  );

  public readonly isLoading$ = this.store.select(selectIsLoading);
  public readonly isInAemPreviewMode$ = this.store.select(selectIsInAemPreviewMode);

  public readonly appPlaceholderComponent = LazyComponentId.AppPlaceholder;

  public constructor(
    private readonly store: Store,
    private readonly themeService: ThemeService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public ngOnInit(): void {
    const element = this.elementRef.nativeElement as HTMLElement;
    element.setAttribute('owcc-version', OWCC_APP_VERSION);
    element.setAttribute('owcc-hash', OWCC_APP_HASH);
  }
}
